import { getDayNumber } from './helpers';
import { Region, RegionsData } from './types';


export class RegionService {
  private regions: Region[] = [];

  // Load regions data from JSON file
  private async loadRegionsData(): Promise<Region[]> {
    try {
      const response = await fetch('data/regions.json');
      if (!response.ok) {
        throw new Error(`Failed to load regions data: ${response.status}`);
      }
      const data: RegionsData = await response.json();
      this.regions = data.regions;
      console.log(`✅ Loaded ${this.regions.length} Quebec regions from JSON`);
      return this.regions;
    } catch (error) {
      console.error('❌ Error loading regions data:', error);
      // Fallback: Use empty array and show error to user
      this.regions = [];
      throw error;
    }
  }

  // Initialize regions data - call this before using any region functions
  public async initializeRegions(): Promise<Region[]> {
    if (this.regions.length === 0) {
      await this.loadRegionsData();
    }
    return this.regions;
  }

  // Ensure regions are loaded before using other methods
  public async ensureRegionsLoaded(): Promise<void> {
    if (this.regions.length === 0) {
      await this.initializeRegions();
    }
  }
  
  // Get all region names for autocomplete
  public getAllRegionNames(): string[] {
    return this.regions.map((region: Region) => region.name);
  }
  
  // Find region by name (case insensitive)
  public findRegionByName(name: string): Region | null {
    const normalizedName = this.normalizeString(name);
    return this.regions.find((region: Region) => 
      this.normalizeString(region.name) === normalizedName
    ) || null;
  }
  
  public getRegionById(id: string): Region | null {
    return this.regions.find((region: Region) => region.id === id) || null;
  }
  
  // Calculate distance between two regions using Haversine formula
  public calculateDistance(region1: Region, region2: Region): number {
    const [lat1, lon1] = region1.coordinates;
    const [lat2, lon2] = region2.coordinates;
    
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c);
  }
  
  // Calculate compass direction from one region to another
  public calculateDirection(fromRegion: Region, toRegion: Region): string {
    const [lat1, lon1] = fromRegion.coordinates;
    const [lat2, lon2] = toRegion.coordinates;
    
    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    
    const angle = Math.atan2(dLon, dLat) * 180 / Math.PI;
    const normalizedAngle = (angle + 360) % 360;
    
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(normalizedAngle / 45) % 8;
    
    return directions[index];
  }
  
  public comparePopulation(region1: Region, region2: Region): string {
    const diff = region2.population - region1.population;
    const percentage = Math.abs(diff) / region1.population * 100;
    
    if (percentage < 10) return "similaire";
    if (diff > 0) return "plus peuplée";
    return "moins peuplée";
  }
  
  // Get deterministic daily region based on date
  public getDailyRegion(date: Date = new Date()): Region {
    const index = getDayNumber(date, this.regions.length);
    const region = this.regions[index];
    return region;
  }
  
  // Normalize string for comparison (remove accents, lowercase, trim)
  private normalizeString(str: string): string {
    return str.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }
  
  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
  
  public getDirectionArrow(direction: string): string {
    const arrows: Record<string, string> = {
      'N' : '⬆️',
      'NE': '↗️',
      'E' : '➡️',
      'SE': '↘️',
      'S' : '⬇️',
      'SW': '↙️',
      'W' : '⬅️',
      'NW': '↖️'
    };
    return arrows[direction] || '❓';
  }
}

// Create and export a singleton instance
export const regionManager = new RegionService();

// For backward compatibility, export functions that use the singleton
export const initializeRegions = () => regionManager.initializeRegions();
export const RegionUtils = regionManager;